import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Bell,
  BellOff,
  CheckCheck,
  Coins,
  Lightbulb,
  Megaphone,
  Package,
  ScrollText,
  TriangleAlert,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dataHoraBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ds";

export const NOTICE_META = {
  AVISO: { label: "Aviso", icon: Megaphone, cls: "border-sky-500/40 bg-sky-500/15 text-sky-300" },
  DICA: {
    label: "Dica",
    icon: Lightbulb,
    cls: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300",
  },
  IMPORTANTE: {
    label: "Importante",
    icon: TriangleAlert,
    cls: "border-red-500/40 bg-red-500/15 text-red-300",
  },
  REPOSICAO: {
    label: "Reposição",
    icon: Package,
    cls: "border-primary/40 bg-primary/15 text-brand",
  },
  FECHAMENTO: {
    label: "Fechamento",
    icon: Coins,
    cls: "border-amber-500/40 bg-amber-500/15 text-amber-300",
  },
  GERAL: {
    label: "Geral",
    icon: ScrollText,
    cls: "border-white/20 bg-white/5 text-muted-foreground",
  },
};

const FILTERS = [
  ["all", "Todos"],
  ["unread", "Não lidos"],
  ["important", "Importantes"],
];

export function NoticeBell({ onGoTo, refreshKey }) {
  const [open, setOpen] = useState(false);
  const [notices, setNotices] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/partner/notices");
      setNotices(data);
    } catch {
      /* silencioso: o sino não pode quebrar a área do parceiro */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Avisos criados depois que a página abriu: recarrega ao abrir o sino.
  const openPanel = () => {
    setOpen(true);
    load();
  };

  const unread = notices.filter((n) => !n.read).length;

  const openNotice = async (n) => {
    if (n.read) return;
    setNotices((prev) =>
      prev.map((x) => (x.id === n.id ? { ...x, read: true, read_at: new Date().toISOString() } : x))
    );
    try {
      await api.post(`/partner/notices/${n.id}/read`);
    } catch (err) {
      toast.error(apiError(err));
      load();
    }
  };

  const readAll = async () => {
    try {
      await api.post("/partner/notices/read-all");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const list = notices.filter((n) =>
    filter === "unread" ? !n.read : filter === "important" ? n.type === "IMPORTANTE" : true
  );

  return (
    <>
      <button
        onClick={openPanel}
        data-testid="partner-notice-bell"
        className="relative shrink-0 rounded-full border border-white/10 p-2 text-muted-foreground transition-colors hover:text-brand"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span
            className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 font-num text-[10px] font-bold text-primary-foreground"
            data-testid="partner-notice-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="partner-notice-center">
          <button
            aria-label="Fechar avisos"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <aside className="relative flex h-full w-full max-w-md flex-col border-l border-white/10 bg-card shadow-2xl">
            <header className="flex items-center justify-between gap-2 border-b border-white/10 px-4 py-4">
              <div>
                <p className="font-display text-lg font-semibold">Avisos</p>
                <p className="text-[11px] text-muted-foreground">
                  {unread > 0 ? `${unread} não lido(s)` : "tudo em dia"}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {unread > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={readAll}
                    data-testid="notice-read-all-btn"
                    className="h-8 gap-1 rounded-full text-[11px]"
                  >
                    <CheckCheck className="h-3.5 w-3.5" /> Marcar tudo
                  </Button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  data-testid="notice-close-btn"
                  className="rounded-full border border-white/10 p-2 text-muted-foreground hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </header>

            <div className="flex gap-1 border-b border-white/10 px-4 py-2">
              {FILTERS.map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setFilter(value)}
                  data-testid={`notice-filter-${value}`}
                  className={`rounded-full px-3 py-1.5 text-[11px] transition-colors ${
                    filter === value
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {list.length === 0 ? (
                <EmptyState
                  icon={BellOff}
                  title="Nenhum aviso aqui"
                  description="Quando a TR3ND enviar um aviso ou algo acontecer com seus pedidos, aparece nesta central."
                />
              ) : (
                <ul className="space-y-3">
                  {list.map((n) => {
                    const meta = NOTICE_META[n.type] || NOTICE_META.GERAL;
                    const Icon = meta.icon;
                    return (
                      <li
                        key={n.id}
                        onClick={() => openNotice(n)}
                        data-testid={`notice-item-${n.id}`}
                        className={`cursor-pointer rounded-xl border p-3 transition-colors ${
                          n.read
                            ? "border-white/10 bg-secondary/30"
                            : "border-primary/30 bg-primary/5"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase ${meta.cls}`}
                          >
                            <Icon className="h-3 w-3" /> {meta.label}
                          </span>
                          {!n.read && (
                            <span
                              className="h-2 w-2 rounded-full bg-primary"
                              data-testid={`notice-unread-dot-${n.id}`}
                            />
                          )}
                          <span className="ml-auto shrink-0 font-num text-[10px] text-muted-foreground">
                            {dataHoraBR(n.created_at)}
                          </span>
                        </div>
                        <p className="mt-2 text-sm font-medium">{n.title}</p>
                        <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">
                          {n.body}
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          {n.action?.label && n.action?.target && (
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openNotice(n);
                                setOpen(false);
                                onGoTo(n.action.target);
                              }}
                              data-testid={`notice-action-${n.id}`}
                              className="h-8 rounded-full text-[11px] font-semibold"
                            >
                              {n.action.label}
                            </Button>
                          )}
                          {n.read && (
                            <span className="text-[10px] text-muted-foreground">✓ Lida</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>
        </div>,
        document.body
      )}
    </>
  );
}
